import { FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Drawer,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItem,
  ListItemText,
  Stack,
  Link,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import WorkIcon from '@mui/icons-material/Work';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import { Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';
import { Logo, Search, SearchType } from '@carcareers/components';
import { routes } from '@carcareers/utils';
import { useAuth } from '@carcareers/hooks';
import { AuthRole } from '@libs/types';

interface AppbarDrawerProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
}

export const AppbarDrawer: FC<AppbarDrawerProps> = ({ isOpen, setOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const renderJobseekerAuthContent = () => {
    if (session?.user?.role !== AuthRole.JOBSEEKER) return null;

    return (
      <>
        {location.pathname?.substring(1) === routes?.JOBSEEKERS && (
          <Box sx={{ p: 2 }}>
            <Search searchType={SearchType.NO_AUTH_JOBSEEKER} />
          </Box>
        )}
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation(`/${routes.JOBSEEKERS}`)}>
              <ListItemIcon>
                <WorkIcon />
              </ListItemIcon>
              <ListItemText primary="Job Search" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(`/${routes.JOBSEEKER_HOME}/${routes.JOBSEEKER_APPLICATIONS}`)}
            >
              <ListItemIcon>
                <AssignmentIcon />
              </ListItemIcon>
              <ListItemText primary="Applied" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation(`/${routes.JOBSEEKER_HOME}/${routes.JOBSEEKER_SAVED}`)}>
              <ListItemIcon>
                <BookmarkIcon />
              </ListItemIcon>
              <ListItemText primary="Saved" />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation(`/${routes.JOBSEEKER_HOME}/${routes.JOBSEEKER_CHAT}`)}>
              <ListItemIcon>
                <ChatBubbleIcon />
              </ListItemIcon>
              <ListItemText primary="Messages" />
            </ListItemButton>
          </ListItem>
        </List>
      </>
    );
  };

  const renderNoAuthContent = () => {
    if (session?.user !== undefined) return null;

    return (
      <>
        {location.pathname?.substring(1) === routes?.JOBSEEKERS && (
          <Box sx={{ p: 2 }}>
            <Search searchType={SearchType.NO_AUTH_JOBSEEKER} />
          </Box>
        )}
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation(`/${routes.JOBSEEKERS}`)}>
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary="Job Seekers" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation(`/${routes.EMPLOYERS}`)}>
              <ListItemIcon>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText primary="Employers" />
            </ListItemButton>
          </ListItem>
        </List>
      </>
    );
  };

  return (
    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
      <IconButton
        size="large"
        aria-label="Menu button"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={() => setOpen(true)}
      >
        <MenuIcon />
      </IconButton>
      <Drawer open={isOpen} onClose={() => setOpen(false)}>
        <Box
          sx={{ width: 300, display: 'flex', flexDirection: 'column', flexGrow: 1 }}
          role="presentation"
          data-testid="appbar-drawer"
        >
          <Logo
            sx={{
              display: 'flex',
              width: 200,
              py: 2,
              m: 'auto',
            }}
          />
          {renderJobseekerAuthContent()}
          {renderNoAuthContent()}
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ marginRight: 2 }}>
              <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Link
                href="tel:8883979797"
                sx={{
                  color: 'text.primary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                (888) 397-9797
              </Link>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Link
                href="mailto:info@carcareers.com"
                sx={{
                  color: 'text.primary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                info@carcareers.com
              </Link>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AppbarDrawer;
